import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, CheckCircle, Edit, Loader2, Home, ImageIcon,
  FileText, Send, AlertTriangle
} from "lucide-react";
import { API } from "@/App";

// Get portal token from localStorage
const getPortalAuth = () => {
  const token = localStorage.getItem("portal_token");
  const client = JSON.parse(localStorage.getItem("portal_client") || "{}");
  return { token, client };
};

export const SellerParticularsReview = () => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [particulars, setParticulars] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [floorplans, setFloorplans] = useState([]);
  const [showAmendments, setShowAmendments] = useState(false);
  const [amendments, setAmendments] = useState("");
  const { token, client } = getPortalAuth();

  useEffect(() => {
    if (!token) {
      navigate("/portal/login");
      return;
    }
    fetchParticulars();
  }, [token, caseId]);

  const fetchParticulars = async () => {
    try {
      const response = await axios.get(`${API}/portal/seller/particulars/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParticulars(response.data.particulars);
      setPhotos(response.data.photos || []);
      setFloorplans(response.data.floorplans || []);
    } catch (error) {
      console.error("Error fetching particulars:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("portal_token");
        navigate("/portal/login");
      } else {
        toast.error("Failed to load property details");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/seller/particulars/${caseId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Property details approved! Your property will be listed shortly.");
      navigate("/portal/seller");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestAmendments = async () => {
    if (!amendments.trim()) {
      toast.error("Please describe the changes you'd like");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/seller/particulars/${caseId}/amendments`, {
        amendments: amendments.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Amendment request sent to the agency");
      navigate("/portal/seller");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send amendments");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!particulars) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold">Details Not Available</h2>
          <p className="text-slate-500 mt-2">Property details haven't been prepared yet.</p>
          <Button className="mt-4" onClick={() => navigate("/portal/seller")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/portal/seller")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-slate-800">Review Property Details</h1>
              <p className="text-sm text-slate-500">{particulars.property_address}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Status Banner */}
        {particulars.status === "amendments_requested" && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Amendments Requested</p>
              <p className="text-sm text-amber-700">
                Your amendment request has been sent. The agency will update the details and send them back for your approval.
              </p>
            </div>
          </div>
        )}

        {/* Property Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="w-5 h-5" />
              Property Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-500">Asking Price</p>
                <p className="font-semibold">£{particulars.asking_price?.toLocaleString() || "TBC"}</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-500">Bedrooms</p>
                <p className="font-semibold">{particulars.bedrooms || "—"}</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-500">Bathrooms</p>
                <p className="font-semibold">{particulars.bathrooms || "—"}</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-500">Property Type</p>
                <p className="font-semibold">{particulars.property_type || "—"}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Description */}
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {particulars.summary && (
              <div>
                <Label className="text-slate-500">Summary</Label>
                <p className="mt-1 text-slate-700">{particulars.summary}</p>
              </div>
            )}
            {particulars.full_description && (
              <div>
                <Label className="text-slate-500">Full Description</Label>
                <p className="mt-1 text-slate-700 whitespace-pre-line">{particulars.full_description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Key Features */}
        {particulars.features?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Key Features</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="grid md:grid-cols-2 gap-2">
                {particulars.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    {feature}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Tenure & Leasehold Information */}
        {(particulars.tenure || particulars.lease_years_remaining) && (
          <Card>
            <CardHeader>
              <CardTitle>Tenure Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm text-slate-500">Tenure</p>
                  <p className="font-semibold">{particulars.tenure || "—"}</p>
                </div>
                {particulars.council_tax_band && (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-sm text-slate-500">Council Tax Band</p>
                    <p className="font-semibold">Band {particulars.council_tax_band}</p>
                  </div>
                )}
                {particulars.epc_rating && (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-sm text-slate-500">EPC Rating</p>
                    <p className="font-semibold">{particulars.epc_rating}</p>
                  </div>
                )}
                {particulars.floor_area_sqft && (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-sm text-slate-500">Floor Area</p>
                    <p className="font-semibold">{particulars.floor_area_sqft} sq ft ({particulars.floor_area_sqm} sq m)</p>
                  </div>
                )}
              </div>

              {/* Leasehold Details */}
              {(particulars.tenure === "Leasehold" || particulars.tenure === "Share of Freehold") && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-slate-700 mb-3">Leasehold Details</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {particulars.lease_years_remaining && (
                      <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                        <p className="text-sm text-slate-500">Years Remaining</p>
                        <p className="font-semibold">{particulars.lease_years_remaining} years</p>
                      </div>
                    )}
                    {particulars.ground_rent && (
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-sm text-slate-500">Ground Rent</p>
                        <p className="font-semibold">£{parseFloat(particulars.ground_rent).toLocaleString()}/year</p>
                        {particulars.ground_rent_review_period && (
                          <p className="text-xs text-slate-400">{particulars.ground_rent_review_period}</p>
                        )}
                      </div>
                    )}
                    {particulars.service_charge && (
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-sm text-slate-500">Service Charge</p>
                        <p className="font-semibold">£{parseFloat(particulars.service_charge).toLocaleString()}/year</p>
                        {particulars.service_charge_review_period && (
                          <p className="text-xs text-slate-400">{particulars.service_charge_review_period}</p>
                        )}
                      </div>
                    )}
                    {particulars.is_shared_ownership && (
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <p className="text-sm text-slate-500">Shared Ownership</p>
                        <p className="font-semibold">{particulars.shared_ownership_percentage}% share</p>
                      </div>
                    )}
                  </div>
                  {particulars.additional_tenure_info && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Additional Information</p>
                      <p className="text-slate-700 mt-1">{particulars.additional_tenure_info}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Room Details */}
        {particulars.rooms?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Room Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {particulars.rooms.map((room, i) => (
                  <div key={i} className="border-b pb-3 last:border-0">
                    <div className="flex justify-between items-start">
                      <p className="font-medium">{room.name}</p>
                      {room.dimensions && (
                        <Badge variant="outline">{room.dimensions}</Badge>
                      )}
                    </div>
                    {room.description && (
                      <p className="text-sm text-slate-500 mt-1">{room.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Photos */}
        {photos.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Property Photos ({photos.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {photos.map((photo) => (
                  <div key={photo.photo_id} className="aspect-video rounded-lg overflow-hidden bg-slate-100">
                    <img
                      src={`${API.replace('/api', '')}${photo.file_url}`}
                      alt={photo.caption || "Property photo"}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Floorplans */}
        {floorplans.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Floorplans ({floorplans.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {floorplans.map((fp) => (
                  <div key={fp.photo_id} className="rounded-lg overflow-hidden bg-slate-100 p-2">
                    <img
                      src={`${API.replace('/api', '')}${fp.file_url}`}
                      alt="Floorplan"
                      className="w-full object-contain"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        {particulars.status !== "approved" && (
          <Card className="sticky bottom-4 shadow-lg">
            <CardContent className="p-4">
              {!showAmendments ? (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowAmendments(true)}
                    disabled={submitting}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Request Changes
                  </Button>
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={handleApprove}
                    disabled={submitting}
                    data-testid="approve-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Approve & Go Live
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label>What changes would you like?</Label>
                    <Textarea
                      value={amendments}
                      onChange={(e) => setAmendments(e.target.value)}
                      placeholder="Please describe the changes you'd like us to make..."
                      rows={4}
                      className="mt-2"
                      data-testid="amendments-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAmendments(false);
                        setAmendments("");
                      }}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={handleRequestAmendments}
                      disabled={submitting || !amendments.trim()}
                      data-testid="send-amendments-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send Amendment Request
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Already Approved */}
        {particulars.status === "approved" && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
              <h3 className="text-lg font-semibold text-green-800">Details Approved</h3>
              <p className="text-green-700 mt-1">
                You approved these details on {new Date(particulars.approved_at).toLocaleDateString()}
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SellerParticularsReview;
